import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  ORDER_KIND_LABEL,
  ORDER_SOURCE_LABEL,
  type OrderKind,
  type OrderSource,
  RACK_TYPE_SHORT,
  RACK_TYPES,
  type RackType,
  type StockResponse,
  todayIso,
} from '@reikraft/shared';
import { Choices, Field, Panel, Segmented, useToast } from '@reikraft/ui';
import { api } from '../api';
import { FormMessage, Hint, MasterSelect, PageHead, useFormErrors } from '../components/common';
import { PartyPicker, partyToBody, type PartyValue } from '../components/PartyPicker';
import { formatMoney } from '../format';
import { useFlag, useTz } from '../session';

const SOURCES: OrderSource[] = ['phone', 'avito', 'referral', 'walk_in', 'site'];

export function OrderNewPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const masters = useFlag('masters');
  const tz = useTz();
  const errs = useFormErrors();

  const [kind, setKind] = useState<OrderKind>((params.get('kind') as OrderKind) || 'repair');
  const [party, setParty] = useState<PartyValue | null>(
    params.get('customerId')
      ? { kind: 'customer', id: params.get('customerId')!, name: params.get('name') ?? 'Клиент' }
      : params.get('organizationId')
        ? { kind: 'organization', id: params.get('organizationId')!, name: params.get('name') ?? 'Организация' }
        : null,
  );
  const [source, setSource] = useState<OrderSource>('phone');
  const [sourceRef, setSourceRef] = useState('');
  const [here, setHere] = useState<'intake' | 'new'>('intake');
  const [needsPickup, setNeedsPickup] = useState(false);
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupDate, setPickupDate] = useState(todayIso(tz));
  const [pickupWindow, setPickupWindow] = useState('');
  const [car, setCar] = useState('');
  const [plate, setPlate] = useState('');
  const [vin, setVin] = useState('');
  const [rackType, setRackType] = useState<RackType | null>(null);
  const [oem, setOem] = useState('');
  const [complaint, setComplaint] = useState('');
  const [completeness, setCompleteness] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [masterId, setMasterId] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(params.get('productId'));
  const [comment, setComment] = useState('');

  const stock = useQuery({
    queryKey: ['stock', ''],
    queryFn: () => api.get<StockResponse>('/admin/stock'),
    enabled: kind === 'sale' || kind === 'exchange',
  });
  const products = (stock.data?.rows ?? []).filter((r) => r.qty > 0 && r.status !== 'archived');
  const product = products.find((p) => p.productId === productId);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; number: string }>('/admin/orders', {
        kind,
        source: kind === 'stock' ? 'internal' : source,
        sourceRef: sourceRef || null,
        status: kind === 'sale' ? 'new' : here === 'intake' ? 'intake' : needsPickup ? 'pickup' : 'new',
        ...(kind === 'stock' ? {} : partyToBody(party)),
        contactName: contactName || null,
        contactPhone: contactPhone || null,
        car: car || null,
        plate: plate || null,
        vin: vin || null,
        rackType,
        oem: oem || null,
        complaint: complaint || null,
        completeness: completeness || null,
        needsPickup: here === 'new' && needsPickup,
        pickupAddress: needsPickup ? pickupAddress || null : null,
        pickupDate: needsPickup ? pickupDate || null : null,
        pickupWindow: needsPickup ? pickupWindow || null : null,
        dueDate: dueDate || null,
        masterId,
        productId: kind === 'sale' || kind === 'exchange' ? productId : null,
        comment: comment || null,
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['counters'] });
      toast(`Заказ ${r.number} создан`);
      void navigate(`/orders/${r.id}`);
    },
    onError: (e) => errs.set(e),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    errs.clear();
    create.mutate();
  };

  const e = errs.errors;
  const needsUnit = kind === 'sale' || kind === 'exchange';
  return (
    <form onSubmit={submit} className="rk-stack" style={{ ['--rk-gap' as string]: '20px' }} noValidate>
      <PageHead title="Новый заказ" crumbs={[{ label: 'Заказы', to: '/orders' }, { label: 'Новый заказ' }]} />
      <FormMessage message={errs.message} />

      <div className="adm-cols">
        <div className="adm-cols__main">
          <Panel num="01" title="Что делаем">
            <div className="rk-stack">
              <Choices
                label="Тип заказа"
                value={kind}
                onChange={setKind}
                options={[
                  { value: 'repair', title: ORDER_KIND_LABEL.repair, note: 'Ремонтируем рейку клиента и возвращаем' },
                  {
                    value: 'exchange',
                    title: ORDER_KIND_LABEL.exchange,
                    note: 'Клиент берёт рейку из фонда, его — после ремонта на склад',
                  },
                  { value: 'sale', title: ORDER_KIND_LABEL.sale, note: 'Продаём рейку со склада без сдачи старой' },
                  { value: 'stock', title: ORDER_KIND_LABEL.stock, note: 'Восстанавливаем выкупленную рейку для фонда' },
                ]}
              />
              {kind !== 'stock' && (
                <div className="rk-grid-form">
                  <Field label="Откуда заказ">
                    <select className="rk-select" value={source} onChange={(ev) => setSource(ev.target.value as OrderSource)}>
                      {SOURCES.map((s) => (
                        <option key={s} value={s}>
                          {ORDER_SOURCE_LABEL[s]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  {source === 'avito' && (
                    <Field label="Ссылка на чат или объявление Авито">
                      <input
                        className="rk-input"
                        value={sourceRef}
                        onChange={(ev) => setSourceRef(ev.target.value)}
                        placeholder="https://www.avito.ru/…"
                      />
                    </Field>
                  )}
                </div>
              )}
            </div>
          </Panel>

          {kind !== 'stock' && (
            <Panel num="02" title="Клиент">
              <div className="rk-stack">
                <PartyPicker value={party} onChange={setParty} errors={e} />
                {party?.kind === 'organization' && (
                  <div className="rk-grid-form">
                    <Field label="Контактное лицо">
                      <input
                        className="rk-input"
                        value={contactName}
                        onChange={(ev) => setContactName(ev.target.value)}
                        placeholder="Механик, мастер-приёмщик"
                      />
                    </Field>
                    <Field label="Телефон контакта" error={e.contactPhone}>
                      <input
                        className="rk-input rk-input--mono"
                        type="tel"
                        value={contactPhone}
                        onChange={(ev) => setContactPhone(ev.target.value)}
                      />
                    </Field>
                  </div>
                )}
              </div>
            </Panel>
          )}

          {needsUnit && (
            <Panel num="03" title="Рейка со склада">
              {stock.isLoading ? (
                <Hint>Загружаем склад…</Hint>
              ) : products.length ? (
                <Field label="Позиция" error={e.productId} hint="Свободный экземпляр зарезервируем под заказ автоматически">
                  <select className="rk-select" value={productId ?? ''} onChange={(ev) => setProductId(ev.target.value || null)}>
                    <option value="">— выберите</option>
                    {products.map((p) => (
                      <option key={p.productId} value={p.productId}>
                        {p.sku} · {p.car} · {RACK_TYPE_SHORT[p.rackType]} · {p.qty} шт ·{' '}
                        {formatMoney(kind === 'exchange' ? p.exchangePriceKop : p.priceKop)}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Hint>На складе нет свободных реек. Для обмена можно оформить заказ сейчас и выбрать рейку позже в карточке заказа.</Hint>
              )}
            </Panel>
          )}

          {kind !== 'sale' && (
            <Panel
              num={needsUnit ? '04' : kind === 'stock' ? '02' : '03'}
              title={kind === 'exchange' ? 'Рейка клиента (сдаёт в обмен)' : 'Рейка и автомобиль'}
            >
              <div className="rk-stack">
                <div className="rk-grid-form">
                  <Field label="Автомобиль" error={e.car}>
                    <input
                      className="rk-input"
                      value={car}
                      onChange={(ev) => setCar(ev.target.value)}
                      placeholder="Например, Kia Rio III"
                    />
                  </Field>
                  <Field label="Госномер">
                    <input
                      className="rk-input rk-input--mono"
                      value={plate}
                      onChange={(ev) => setPlate(ev.target.value)}
                      placeholder="А000АА 77 или «снята»"
                    />
                  </Field>
                  <Field label="VIN">
                    <input className="rk-input rk-input--mono" value={vin} onChange={(ev) => setVin(ev.target.value)} />
                  </Field>
                  <Field label="Номер на корпусе (OEM)">
                    <input className="rk-input rk-input--mono" value={oem} onChange={(ev) => setOem(ev.target.value)} />
                  </Field>
                </div>
                <Field label="Тип рейки">
                  <Segmented
                    label="Тип рейки"
                    value={rackType}
                    onChange={setRackType}
                    options={RACK_TYPES.map((t) => ({ value: t, label: RACK_TYPE_SHORT[t] }))}
                  />
                </Field>
                <Field label="Жалоба клиента">
                  <textarea
                    className="rk-textarea"
                    rows={3}
                    value={complaint}
                    onChange={(ev) => setComplaint(ev.target.value)}
                    placeholder="Стук, люфт, течь, тугой руль…"
                  />
                </Field>
                <Field label="Комплектность">
                  <input
                    className="rk-input"
                    value={completeness}
                    onChange={(ev) => setCompleteness(ev.target.value)}
                    placeholder="С тягами, без наконечников"
                  />
                </Field>
              </div>
            </Panel>
          )}
        </div>

        <div className="adm-cols__side adm-cols__side--sticky">
          {kind !== 'sale' && kind !== 'stock' && (
            <Panel title="Где рейка">
              <div className="rk-stack">
                <Choices
                  label="Где рейка"
                  value={here}
                  onChange={setHere}
                  min={140}
                  options={[
                    { value: 'intake', title: 'Уже у нас', note: 'Сразу на приёмку' },
                    { value: 'new', title: 'Ещё у клиента', note: 'Привезут или заберём' },
                  ]}
                />
                {here === 'new' && (
                  <>
                    <label className="rk-check">
                      <input type="checkbox" checked={needsPickup} onChange={(ev) => setNeedsPickup(ev.target.checked)} />
                      Забрать у клиента
                    </label>
                    {needsPickup && (
                      <>
                        <Field label="Адрес забора">
                          <input className="rk-input" value={pickupAddress} onChange={(ev) => setPickupAddress(ev.target.value)} />
                        </Field>
                        <div className="rk-grid-form" style={{ ['--rk-min' as string]: '120px' }}>
                          <Field label="Дата">
                            <input
                              className="rk-input rk-input--mono"
                              type="date"
                              value={pickupDate}
                              onChange={(ev) => setPickupDate(ev.target.value)}
                            />
                          </Field>
                          <Field label="Время">
                            <input
                              className="rk-input rk-input--mono"
                              value={pickupWindow}
                              onChange={(ev) => setPickupWindow(ev.target.value)}
                              placeholder="12:00–15:00"
                            />
                          </Field>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </Panel>
          )}
          <Panel title="Исполнение">
            <div className="rk-stack">
              {kind !== 'sale' && (
                <Field label="План выдачи" hint="Если не указать — через несколько рабочих дней">
                  <input className="rk-input rk-input--mono" type="date" value={dueDate} onChange={(ev) => setDueDate(ev.target.value)} />
                </Field>
              )}
              {masters && kind !== 'sale' && (
                <Field label="Мастер">
                  <MasterSelect value={masterId} onChange={setMasterId} />
                </Field>
              )}
              <Field label="Комментарий для команды">
                <textarea className="rk-textarea" rows={2} value={comment} onChange={(ev) => setComment(ev.target.value)} />
              </Field>
              {product && (
                <div className="rk-kv">
                  <span>К оплате</span>
                  <b className="rk-mono">{formatMoney(kind === 'exchange' ? product.exchangePriceKop : product.priceKop)}</b>
                </div>
              )}
              <button type="submit" className="rk-btn rk-btn--primary rk-btn--lg rk-btn--block" disabled={create.isPending}>
                {create.isPending ? 'Создаём…' : 'Создать заказ'}
              </button>
            </div>
          </Panel>
        </div>
      </div>
    </form>
  );
}
